import { memo, useMemo, useState } from "react";
import { formatTranscriptLines } from "../../utils/formatTranscriptLines";

/** Match SOAP note body typography (EnhancedSoapNoteDisplay / InlineEditableLine). */
const TRANSCRIPT_LINE_CLASS =
  "text-lg text-gray-700 leading-relaxed max-w-none";

function ConversationViewer({ conversation }) {
  const safeConversation = conversation || "";
  const displayConversation = useMemo(
    () => formatTranscriptLines(safeConversation),
    [safeConversation]
  );

  const lines = useMemo(
    () => displayConversation.split("\n").filter((line) => line.trim() !== ""),
    [displayConversation],
  );

  // Check if this looks like a speaker-formatted transcript
  const isSpeakerFormatted = useMemo(
    () => lines.some((line) => /^Speaker\s+\d+:/.test(line)),
    [lines],
  );

  // Define color mapping for up to 5 speakers
  const speakerColors = {
    1: "text-blue-700",
    2: "text-green-700",
    3: "text-purple-700",
    4: "text-orange-700",
    5: "text-red-700",
  };

  const parsedLines = useMemo(
    () =>
      lines.map((line) => {
        const match = line.match(/^Speaker\s+(\d+):/);
        const role = match
          ? {
              label: `Speaker ${parseInt(match[1], 10)}`,
              color: speakerColors[parseInt(match[1], 10)] || "text-gray-700",
            }
          : null;
        return {
          line,
          role,
          text: role ? line.replace(/^Speaker\s+\d+:/, "").trim() : line,
        };
      }),
    [lines],
  );

  const [scrollTop, setScrollTop] = useState(0);
  const ROW_HEIGHT = 34;
  const VIEWPORT_HEIGHT = 420;
  const OVERSCAN = 20;
  const useVirtualized = parsedLines.length > 400;
  const totalHeight = parsedLines.length * ROW_HEIGHT;
  const visibleCount = Math.ceil(VIEWPORT_HEIGHT / ROW_HEIGHT) + OVERSCAN * 2;
  const startIndex = useVirtualized
    ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN)
    : 0;
  const endIndex = useVirtualized
    ? Math.min(parsedLines.length, startIndex + visibleCount)
    : parsedLines.length;
  const topPadding = startIndex * ROW_HEIGHT;
  const bottomPadding = Math.max(0, totalHeight - endIndex * ROW_HEIGHT);

  // Handle null or empty conversation
  if (!displayConversation.trim()) {
    return (
      <div className="space-y-3 p-4">
        <p className="text-gray-500 italic">No transcription available</p>
      </div>
    );
  }

  // If not formatted, show plain text
  if (!isSpeakerFormatted) {
    return (
      <div className="space-y-3">
        {lines.map((line, i) => (
          <p key={`cv-${i}`} className={TRANSCRIPT_LINE_CLASS}>
            {line}
          </p>
        ))}
      </div>
    );
  }

  // If formatted with speakers, render each line clearly
  return (
    <div
      className={useVirtualized ? "space-y-3 p-4 h-full overflow-y-auto" : "space-y-3 p-4"}
      onScroll={useVirtualized ? (e) => setScrollTop(e.currentTarget.scrollTop) : undefined}
    >
      {useVirtualized && topPadding > 0 && <div style={{ height: `${topPadding}px` }} />}
      {parsedLines.slice(startIndex, endIndex).map((entry, idx) => {
        const role = entry.role;
        const key = startIndex + idx;
        if (role) {
          return (
            <div key={key} className={`text-left min-h-[34px] ${TRANSCRIPT_LINE_CLASS}`}>
              <span className={`font-semibold ${role.color}`}>
                {role.label}:
              </span>{" "}
              <span>{entry.text}</span>
            </div>
          );
        }
        return (
          <p key={key} className={`min-h-[34px] ${TRANSCRIPT_LINE_CLASS}`}>
            {entry.text}
          </p>
        );
      })}
      {useVirtualized && bottomPadding > 0 && <div style={{ height: `${bottomPadding}px` }} />}
    </div>
  );
}

export default memo(ConversationViewer);
